import { useCallback } from "react";
import { useRefinementList } from "react-instantsearch-hooks-web";
import { AlgoliaFacet } from "./FilterView";
import React, { memo } from "react";
import { Box } from "@shared-components/Box/Box";
import { Grid } from "@shared-components/Grid/Grid";
import { Button } from "theme-ui";
import { RangeSlider } from "./RangeSlider";

type RefinementListItemProps = {
  attribute: AlgoliaFacet;
  activeLetter?: string;
  handleCurrentItems: (items: any) => void;
  label: string;
  activeFilter: string;
  texts: {
    alcohol: string;
  };
};

export const RefinementListItem: React.FC<RefinementListItemProps> = memo(
  ({
    attribute,
    activeLetter,
    texts,
    handleCurrentItems,
    label,
    activeFilter,
  }) => {
    const useRangeSlider =
      attribute === "sizegroup" || attribute === "abv" || attribute === "year";

    const sortBy = useCallback((a: any, b: any) => {
      return Number(a.name) - Number(b.name);
    }, []);

    const { items, refine, canRefine } = useRefinementList({
      attribute,
      sortBy: attribute === "sizegroup" ? sortBy : ["name:asc"],
      limit: 1000,
    });

    if (activeFilter === label) {
      handleCurrentItems(items);
    }

    const handleRefinementChange = (attributeValue: string) => {
      refine(attributeValue);
    };

    if (
      items &&
      items.length !== 0 &&
      (attribute === "year" || attribute === "abv" || attribute === "sizegroup")
    ) {
      // Define default range slider properties
      const defaultProps = {
        attribute,
        step: 1,
      };

      // Set range slider properties based on 'attribute'
      const rangeProps =
        attribute === "abv"
          ? {
              ...defaultProps,
              label: texts.alcohol,
              step: 0.1, // Set step to 0.1 for 'abv'
              unit: "%", // Add unit of measurement specific to 'abv'
            }
          : attribute === "sizegroup"
          ? {
              ...defaultProps,
              step: 0.1, // Set step to 0.1 for 'sizegroup'
              unit: " cl", // Add unit of measurement specific to 'sizegroup'
            }
          : {
              ...defaultProps, // Use default properties if 'attribute' is 'year'
            };

      return (
        <Box
          sx={{
            display: "flex",
            h: "full",
            justifyContent: "center",
            px: 11,
            py: 20,
            mt: 12,
          }}
        >
          <RangeSlider {...rangeProps} />
        </Box>
      );
    }

    if (!useRangeSlider && items && items.length !== 0) {
      return (
        <Grid
          sx={{
            gridAutoColumns: "max-content",
            h: "full",
            gridAutoFlow: "column",
            gridTemplateRows: "repeat(8, minmax(0, 1fr))",
            rowGap: 3,
            columnGap: 5,
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            py: 2,
            "&::-webkit-scrollbar": {
              WebkitAppearance: "none",
              width: "4px",
              backgroundColor: "lighterGray",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "mediumGray",
              borderRadius: 4,
              boxShadow: "0 0 1px rgba(255, 255, 255, 0.5)",
            },
          }}
        >
          {!useRangeSlider &&
            items
              .filter(
                ({ value }) =>
                  !activeLetter ||
                  (activeLetter && value.startsWith(activeLetter))
              )
              .map(({ label, value, isRefined }, key) => {
                return (
                  <Button
                    disabled={!canRefine}
                    onClick={() => handleRefinementChange(value)}
                    key={key}
                    sx={{
                      display: "block",
                      paddingInline: 3,
                      textAlign: "left",
                      backgroundColor: isRefined ? "mediumGray" : "transparent",
                      color: isRefined ? "white" : "black",
                      width: "max-content",
                      ":hover": {
                        bg: isRefined ? "darkGray" : "gray",
                      },
                      ":focus": {
                        boxShadow: "none",
                      },
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
        </Grid>
      );
    }

    return null;
  }
);
